import { useEffect, useState } from "react";
import { ApiError, fetchScheduledJobs, type ScheduledJobDto } from "../api";
import { FieldHelp } from "../components/FieldHelp";
import { PageHelper } from "../components/PageHelper";
import { exportRowsAsCsv } from "../exportCsv";
import { sortGlyph, useSort } from "../hooks/useSort";

const STATUS_RANK: Record<string, number> = { OVERDUE: 0, UNKNOWN: 1, DUE_SOON: 2, HEALTHY: 3 };

function StatusPill({ status }: { status: string }) {
  const label = status === "DUE_SOON" ? "DUE SOON" : status;
  const cls = status === "HEALTHY" ? "st-champion" : status === "OVERDUE" ? "st-challenger" : "chip";
  const color = status === "OVERDUE" ? "var(--bad)" : status === "DUE_SOON" ? "var(--warn)" : undefined;
  return (
    <span className={`chip ${cls}`} style={color ? { color, borderColor: color } : undefined}>
      <span style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%", marginRight: 6, background: "currentColor" }} />
      {label}
    </span>
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return iso.slice(0, 16).replace("T", " ");
}

export function ScheduledJobsPage({ token, onUnauthorized }: { token: string; onUnauthorized: () => void }) {
  const [data, setData] = useState<{ as_of: string; jobs: ScheduledJobDto[]; counts: Record<string, number> } | null>(null);
  const [error, setError] = useState<string | null>(null);

  function load() {
    fetchScheduledJobs(token)
      .then(setData)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) {
          onUnauthorized();
          return;
        }
        setError(err instanceof ApiError ? err.message : "failed to load scheduled jobs");
      });
  }

  useEffect(() => {
    load();
  }, [token]);

  const jobs = data?.jobs ?? [];
  // Default view is worst-first (the server already sorts this way);
  // useSort's own `sorted` falls back to the input order until the user
  // explicitly clicks a column header, so no separate fallback is needed.
  const { sorted: rows, sortKey, sortDir, toggleSort } = useSort<ScheduledJobDto>(jobs, (job, key) => {
    if (key === "status") return STATUS_RANK[job.status] ?? 9;
    if (key === "label") return job.label;
    if (key === "cadence") return job.freq_label;
    if (key === "last_run_at") return job.last_run_at ?? "";
    return "";
  });

  function exportCsv() {
    if (!data) return;
    exportRowsAsCsv(
      data.jobs as unknown as Record<string, unknown>[],
      [
        { key: "label", label: "Job" },
        { key: "freq_label", label: "Frequency" },
        { key: "status", label: "Status" },
        { key: "last_run_at", label: "Last successful run" },
        { key: "reason", label: "Note" },
      ],
      "scheduled_jobs.csv",
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Scheduled jobs</h1>
          <div className="page-sub">Did every cron/timer actually run, or did something silently stop? Cadence-aware.</div>
        </div>
      </div>

      <PageHelper>
        Every cron / timer that keeps the system current, in one place — with cadence-aware{" "}
        <FieldHelp term="scheduled_job_status" />OVERDUE detection. A job's staleness is judged against
        its OWN publishing rhythm (daily / weekly / fortnightly / monthly), not one global cutoff — a
        fortnightly source 10 days old is healthy, a daily one 5 days old is not. Sorted worst-first: if
        something silently stopped running, it's at the top. This is the companion to the Data ops
        page's per-source freshness view — that page asks "is the DATA current," this one asks "did the
        JOB actually run."
      </PageHelper>

      {error && <div className="card dc-bad" style={{ marginBottom: 12 }}>{error}</div>}

      {data && (
        <>
          <div className="kpis">
            <div className="kpi">
              <div className="l">Healthy</div>
              <div className="v" style={{ color: "var(--ok)" }}>{data.counts.HEALTHY ?? 0}</div>
            </div>
            <div className="kpi">
              <div className="l">Overdue</div>
              <div className="v" style={{ color: "var(--bad)" }}>{data.counts.OVERDUE ?? 0}</div>
            </div>
            <div className="kpi">
              <div className="l">Due soon</div>
              <div className="v" style={{ color: "var(--warn)" }}>{data.counts.DUE_SOON ?? 0}</div>
            </div>
            <div className="kpi">
              <div className="l">Unavailable here</div>
              <div className="v">{data.counts.UNKNOWN ?? 0}</div>
            </div>
            <div className="kpi">
              <div className="l">Total jobs</div>
              <div className="v">{data.jobs.length}</div>
            </div>
          </div>

          <div className="card" style={{ padding: 0, marginBottom: 16, overflow: "hidden" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px" }}>
              <span className="mut" style={{ fontSize: "0.78rem" }}>As of {fmtDate(data.as_of)} UTC</span>
              <button className="btn ghost sm" onClick={exportCsv}>↓ CSV</button>
            </div>
            <table className="stocks-table">
              <thead>
                <tr>
                  <th className="l clickable" onClick={() => toggleSort("label")}>Job {sortGlyph("label", sortKey, sortDir)}</th>
                  <th className="clickable" onClick={() => toggleSort("cadence")}>Frequency {sortGlyph("cadence", sortKey, sortDir)}</th>
                  <th className="clickable" onClick={() => toggleSort("status")}>
                    Status <FieldHelp term="scheduled_job_status" /> {sortGlyph("status", sortKey, sortDir)}
                  </th>
                  <th className="clickable" onClick={() => toggleSort("last_run_at")}>
                    Last successful run {sortGlyph("last_run_at", sortKey, sortDir)}
                  </th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((j) => (
                  <tr key={j.key} style={j.status === "OVERDUE" ? { background: "color-mix(in srgb, var(--bad) 6%, transparent)" } : undefined}>
                    <td className="l">
                      <div style={{ fontFamily: "var(--font-display)", fontWeight: 600 }}>{j.label}</div>
                      <div className="mut" style={{ fontSize: "0.72rem" }}>{j.desc}</div>
                    </td>
                    <td className="mono" style={{ fontSize: "0.78rem" }}>{j.freq_label}</td>
                    <td><StatusPill status={j.status} /></td>
                    <td className="mono" style={{ fontSize: "0.8rem" }}>{fmtDate(j.last_run_at)}</td>
                    <td className="mut" style={{ fontSize: "0.76rem" }}>{j.reason ?? j.note ?? "—"}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={5} className="mut" style={{ textAlign: "center", padding: 20 }}>No scheduled jobs registered.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!data && !error && <div className="mut">Loading…</div>}
    </>
  );
}
